import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostTarget } from 'types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import { ProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { ChannelItemState } from './ChannelListItem'
import { getContentWarning } from './channelWarnings'
import { isStoryChannel } from '../utils'

const STORY_LIFETIME_MS = 24 * 60 * 60 * 1000

const isExpiredStory = (channel: DesignChannel | ChannelType | undefined, postedAt: string | undefined) => {
  if (!isStoryChannel(channel)) return false
  if (!postedAt) return false
  const postedMs = Date.parse(postedAt)
  if (Number.isNaN(postedMs)) return false
  return Date.now() - postedMs >= STORY_LIFETIME_MS
}

const expiredStoryTooltip = (channel: DesignChannel | ChannelType | undefined) => {
  const network = channel === DesignChannel.FacebookStory ? 'Facebook' : 'Instagram'
  return `Since 24 hours have passed, your Story is no longer live. You can still find it in your ${network} archive.`
}

interface DerivedState {
  state: ChannelItemState
  warningLabel?: string
}

const deriveState = (
  isConnected: boolean,
  target: ScheduledPostTarget | undefined,
  isPosted: boolean,
  channel: DesignChannel | ChannelType | undefined,
  isCarousel: boolean,
): DerivedState => {
  if (isPosted && target?.status === 'published') {
    return { state: isExpiredStory(channel, target.published_at) ? 'posted-expired' : 'posted' }
  }
  if (target?.status === 'failed') return { state: 'failed' }
  if (target?.status === 'queued' || target?.status === 'processing' || target?.status === 'retrying') {
    return { state: 'processing' }
  }
  if (!isConnected) return { state: 'unconnected' }
  if (isPosted && target) return { state: 'post-now' }
  if (target) {
    const warningLabel = getContentWarning(target, { isCarousel })
    if (warningLabel) return { state: 'warning', warningLabel }
    return { state: 'connected' }
  }
  return { state: 'connected-disabled' }
}

export interface ChannelRowData {
  provider: ExternalIntegrationEnum
  integrationId?: number
  accountName?: string
  accountAvatarUrl?: string
  state: ChannelItemState
  postUrl?: string
  expiredTooltip?: string
  warningLabel?: string
}

interface BuildChannelRowsArgs {
  crossPostGroup: readonly ExternalIntegrationEnum[]
  integrations: ExternalIntegration[]
  targets: ScheduledPostTarget[]
  accountIdsNeedReauth: number[]
  isPosted: boolean
  channel: DesignChannel | ChannelType | undefined
  isCarousel: boolean
}

export const buildChannelRows = ({
  crossPostGroup,
  integrations,
  targets,
  accountIdsNeedReauth,
  isPosted,
  channel,
  isCarousel,
}: BuildChannelRowsArgs): ChannelRowData[] => {
  const rows: ChannelRowData[] = []

  for (const provider of crossPostGroup) {
    const providerIntegrations = integrations.filter((i) => i.provider === provider)

    if (providerIntegrations.length >= 1) {
      for (const integration of providerIntegrations) {
        const target = targets.find((t) => t.provider === provider && t.external_integration_id === integration.id)
        const needsReauth = accountIdsNeedReauth.includes(integration.id)
        const { name, avatar } = extractProfileInfo(integration)
        const { state, warningLabel } = deriveState(!needsReauth, target, isPosted, channel, isCarousel)
        rows.push({
          provider,
          integrationId: integration.id,
          accountName: name || integration.name || ProviderToName[provider],
          accountAvatarUrl: avatar || integration.picture || undefined,
          state,
          postUrl: target?.post_url,
          expiredTooltip: state === 'posted-expired' ? expiredStoryTooltip(channel) : undefined,
          warningLabel,
        })
      }
      continue
    }

    const { state, warningLabel } = deriveState(false, undefined, isPosted, channel, isCarousel)
    rows.push({
      provider,
      integrationId: undefined,
      accountName: undefined,
      accountAvatarUrl: undefined,
      state,
      postUrl: undefined,
      expiredTooltip: undefined,
      warningLabel,
    })
  }

  return rows
}
